import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// --- Refresh silencieux du token (sessions longues : téléconsultation) ---
// Sur un 401, tente UN seul /auth/refresh (dédoublonné entre requêtes concurrentes)
// puis rejoue la requête d'origine. Évite les boucles et n'agit pas sur les routes d'auth.
let _refreshPromise = null;
const _noRefresh = ["/auth/refresh", "/auth/login", "/auth/register", "/auth/me", "/auth/logout"];

export function refreshSession() {
  if (!_refreshPromise) {
    _refreshPromise = api.post("/auth/refresh").finally(() => { _refreshPromise = null; });
  }
  return _refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { response, config } = error;
    if (!response || response.status !== 401 || !config || config._retry) {
      return Promise.reject(error);
    }
    const url = config.url || "";
    if (_noRefresh.some((p) => url.includes(p))) {
      return Promise.reject(error);
    }
    config._retry = true;
    try {
      await refreshSession();
      return api(config);
    } catch (e) {
      return Promise.reject(error);
    }
  }
);

export const fmtXof = (n) =>
  `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(n ?? 0))} FCFA`;

export const COVER_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='240'%3E%3Crect width='320' height='240' fill='%23E2E8F0'/%3E%3C/svg%3E";
export const coverSrc = (cover) =>
  !cover ? COVER_PLACEHOLDER : cover.startsWith("http") ? cover : `${API}/files/${cover}`;

export const downloadUrl = (token) => `${API}/download/${token}`;
export const streamUrl = (token) => `${API}/stream/${token}`;
export const wsUrl = (path) => `${(process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws")}/api${path}`;
export const invoiceUrl = (orderId) => `${API}/orders/${orderId}/invoice`;

export const fmtDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
};

export const fmtDateTime = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const STATUS_LABELS = {
  en_attente: "En attente",
  payee: "Payée",
  confirmee: "Confirmée",
  en_preparation: "En préparation",
  expediee: "Expédiée",
  livree: "Livrée",
  remboursee: "Remboursée",
  annulee: "Annulée",
};

export const STATUS_STYLES = {
  en_attente: "text-amber-700 border-amber-300 bg-amber-50",
  payee: "text-emerald-700 border-emerald-300 bg-emerald-50",
  confirmee: "text-emerald-700 border-emerald-300 bg-emerald-50",
  en_preparation: "text-sky-700 border-sky-300 bg-sky-50",
  expediee: "text-indigo-700 border-indigo-300 bg-indigo-50",
  livree: "text-emerald-700 border-emerald-300 bg-emerald-50",
  remboursee: "text-red-700 border-red-300 bg-red-50",
  annulee: "text-slate-600 border-slate-300 bg-slate-100",
};

export const CATEGORIES = [
  "Formations médicales",
  "Outils de gestion de l'information médicale",
  "Applications informatiques",
  "Autres produits digitaux",
];

export const CATEGORY_SHORT = {
  "Formations médicales": "Formations",
  "Outils de gestion de l'information médicale": "Outils GIM",
  "Applications informatiques": "Applications",
  "Autres produits digitaux": "Autres",
};

export function apiError(e, fallback = "Une erreur est survenue") {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return fallback;
}
